import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { Link, useNavigate } from 'react-router';
import { BellRing } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useCustomerAgentAvailable } from '@/features/whatsapp/hooks/useCustomerAgentAvailable';
import { createAlertController, type AlertController } from '../lib/alert-controller';
import type { AlertChannelFactory } from '../lib/alert-leader';
import { ALERT_SOURCES, type AlertSource } from '../lib/alert-sources';
import { formatClock } from '../lib/wait-time';
import styles from './ConversationAlerts.module.css';

/* ==========================================================================
   Alertas globais de atendimento (change customer-agent-handoff, D19)

   Em qualquer tela do painel, para quem vê alguma fonte de alerta
   (`lib/alert-sources.ts`): faixa com uma linha por fonte, não dispensável,
   que some sozinha em 0; som na pendência nova e a cada 60 s só na aba
   líder; "Ativar som" quando nenhuma aba liberou o áudio; título "(N) ";
   notificação do navegador na líder oculta ou sem foco, sem nome, telefone
   ou texto; "Silenciar por 30 min" neste navegador. As decisões ficam no
   `alert-controller.ts`; aqui só a leitura das fontes e a faixa. Um só
   `ConversationAlerts` no painel (o do `PanelLayout`): fonte nova entra no
   registro, nunca num segundo componente. Tudo em `ph-no-capture`.
   ========================================================================== */

export interface ConversationAlertsProps {
  /** As fontes registradas; os testes passam as deles. */
  sources?: readonly AlertSource[];
  /** O canal entre as abas; o `BroadcastChannel` do navegador, por padrão (os testes passam um falso). */
  channelFactory?: AlertChannelFactory;
}

/**
 * A faixa, o som, o título e a notificação do atendimento. Sem nenhuma fonte
 * que o usuário veja (a Cozinha), não renderiza nada: nenhuma leitura,
 * nenhum canal, nenhum som.
 */
export function ConversationAlerts({ sources = ALERT_SOURCES, channelFactory }: ConversationAlertsProps) {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const tenantId = useAuthStore((state) => state.selectedTenant?.id ?? '');
  const visible = useMemo(
    () => sources.filter((source) => permissions?.includes(source.permission) ?? false),
    [sources, permissions],
  );
  if (!tenantId || visible.length === 0) return null;
  return <ConversationAlertsContent key={tenantId} tenantId={tenantId} sources={visible} channelFactory={channelFactory} />;
}

interface ContentProps {
  tenantId: string;
  sources: readonly AlertSource[];
  channelFactory?: AlertChannelFactory;
}

function ConversationAlertsContent({ tenantId, sources, channelFactory }: ContentProps) {
  const navigate = useNavigate();
  const controller = useMemo(
    () => createAlertController({ tenantId, sources, channelFactory }),
    [tenantId, sources, channelFactory],
  );

  useEffect(() => {
    controller.start();
    return () => controller.stop();
  }, [controller]);

  useEffect(() => {
    controller.setOpenHandler((href) => navigate(href));
  }, [controller, navigate]);

  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);

  return (
    <>
      {sources.map((source) => (
        <SourceProbe key={source.id} source={source} controller={controller} />
      ))}
      {snapshot.total > 0 && (
        // Não dispensável: some sozinha quando não há mais pendência.
        <div className={`${styles.banner} ph-no-capture`} role="status" data-testid="conversation-alerts">
          <BellRing size={16} className={styles.icon} aria-hidden="true" />
          <ul className={styles.lines}>
            {snapshot.lines.map(({ sourceId, state }) => {
              const dismiss = sources.find((source) => source.id === sourceId)?.dismiss;
              return (
                <li key={sourceId} className={styles.line} data-alert-source={sourceId}>
                  <span className={styles.text}>{state.line}</span>
                  <Link to={state.link.to} className={styles.link}>
                    {state.link.label}
                  </Link>
                  {state.dismissLabel && dismiss && (
                    <button type="button" className={styles.action} onClick={dismiss}>
                      {state.dismissLabel}
                    </button>
                  )}
                  {state.note && <span className={styles.note}>{state.note}</span>}
                </li>
              );
            })}
          </ul>
          <div className={styles.actions}>
            {!snapshot.anyAudioUnlocked && (
              <button type="button" className={styles.action} onClick={() => controller.enableSound()}>
                Ativar som
              </button>
            )}
            {snapshot.notificationPermission === 'default' && (
              <button type="button" className={styles.action} onClick={() => void controller.requestNotifications()}>
                Ativar notificações
              </button>
            )}
            {snapshot.mutedUntil !== null ? (
              <span className={styles.muted}>
                Som silenciado até {formatClock(new Date(snapshot.mutedUntil).toISOString())}
              </span>
            ) : (
              <button type="button" className={styles.action} onClick={() => controller.mute()}>
                Silenciar por 30 min
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

interface SourceProbeProps {
  source: AlertSource;
  controller: AlertController;
}

/** Só consulta a fonte com a área do Atendente IA disponível para a permissão dela. */
function SourceProbe({ source, controller }: SourceProbeProps) {
  const { available } = useCustomerAgentAvailable(source.permission);
  useEffect(() => {
    if (!available) controller.setSourceState(source.id, null);
  }, [available, controller, source.id]);
  return available ? <SourceReporter source={source} controller={controller} /> : null;
}

function SourceReporter({ source, controller }: SourceProbeProps) {
  const state = source.useAlertState();
  // O estado volta como objeto novo a cada render: entrega pelo conteúdo.
  const serialized = state ? JSON.stringify(state) : '';
  useEffect(() => {
    controller.setSourceState(source.id, serialized ? JSON.parse(serialized) : null);
  }, [serialized, controller, source.id]);
  useEffect(() => () => controller.setSourceState(source.id, null), [controller, source.id]);
  return null;
}
